import brandInfo from 'src/environments/brand.json';
import { Vehicle } from '../_models/vehicle';
import {
  hasExistingClaims,
  mileageOrAgeExceeded,
  productExist,
  success,
} from '../_actions/modal-component.actions';
import { ratesLoaded, ratesLoadedFailed } from '../_actions/rates.actions';
import {
  loadVehicle,
  loadVehicleImage,
} from '../_actions/vehicle.actions';
import { hideInlineLoader } from '../_actions/inline-loader.actions';

export type HyundaiServiceDriveRatesOutcome =
  | 'success'
  | 'productExist'
  | 'hasExistingClaims'
  | 'mileageOrAgeExceeded'
  | null;

export function isHppGenesisServiceDriveMultiProduct(): boolean {
  const appFlow = sessionStorage.getItem('appFlowState') || '';
  return (
    (brandInfo.brand === 'HPP' || brandInfo.brand === 'Genesis' || brandInfo.brand === 'Kia') &&
    appFlow.toLowerCase().includes('servicedrive')
  );
}

/** Skip per-product failure handling until all products in the response are evaluated. */
export function shouldDeferHppGenesisServiceDriveProductOutcome(): boolean {
  return isHppGenesisServiceDriveMultiProduct();
}

function productList(rates: any): { statusCode?: number }[] {
  return rates?.data?.productDetailsList ?? [];
}

export function hasEligibleRatesProduct(rates: any): boolean {
  return productList(rates).some((p) => p.statusCode === 100);
}

export function resolveHyundaiServiceDriveAfterLoop(
  rates: any
): HyundaiServiceDriveRatesOutcome {
  if (!isHppGenesisServiceDriveMultiProduct()) {
    return null;
  }

  const list = productList(rates);
  if (!list.length) {
    return null;
  }

  if (hasEligibleRatesProduct(rates)) {
    return 'success';
  }

  if (list.some((p) => p.statusCode === 106)) {
    return 'hasExistingClaims';
  }

  if (list.some((p) => p.statusCode === 105)) {
    return 'mileageOrAgeExceeded';
  }

  if (list.some((p) => p.statusCode === 102)) {
    return 'productExist';
  }

  return null;
}

export function buildRatesVehicleRequest(
  action: any,
  rates: any,
  vehicle: Vehicle | null,
  odometer?: number
): Vehicle {
  const mileage =
    odometer ??
    (action.odometer &&
    typeof action.odometer === 'string' &&
    action.odometer.includes(',')
      ? Number(action.odometer.replace(',', ''))
      : Number(action.odometer));

  const request: Vehicle = {
    isElectric: action.isElectric ?? null,
    make: rates.data.vehicleMake,
    makeExternalId: '',
    makeSfId: '',
    model: rates.data.vehicleModel,
    modelExternalId: '',
    modelSfId: '',
    modelNumber: '',
    purchaseDate: '',
    inserviceDate: rates.data?.inServiceDate ?? null,
    mfrWarrantyDate: '',
    deliveryDate: '',
    exteriorColor: '',
    referenceNumber1: '',
    referenceNumber2: '',
    referenceNumber3: '',
    referenceNumber4: '',
    registeredState: action.customerState,
    mileage,
    trim: rates.data.vehicleTrim,
    vehicleCondition: '',
    vehicleExternalId: '',
    vehicleSfId: '',
    vehicleType: '',
    trimExternalId: '',
    trimSfId: '',
    vin: action.vin,
    year: rates.data.vehicleYear,
  };

  if (vehicle) {
    request.isElectric = vehicle.isElectric;
  }

  return request;
}

export function applyHyundaiServiceDriveOutcome(
  outcome: HyundaiServiceDriveRatesOutcome,
  rates: any,
  action: any,
  vehicle: Vehicle | null,
  dispatch: (storeAction: any) => void,
  odometer?: number
) {
  if (!outcome) {
    return null;
  }

  const request = buildRatesVehicleRequest(
    action,
    rates,
    vehicle,
    odometer
  );

  switch (outcome) {
    case 'success':
      dispatch(loadVehicle(request));
      dispatch(ratesLoaded(rates));
      dispatch(loadVehicleImage(request));
      dispatch(hideInlineLoader());
      return success();
    case 'productExist':
      dispatch(loadVehicle(request));
      dispatch(ratesLoaded(rates));
      dispatch(loadVehicleImage(request));
      dispatch(hideInlineLoader());
      return productExist();
    case 'hasExistingClaims':
      dispatch(ratesLoadedFailed(rates));
      dispatch(hideInlineLoader());
      return hasExistingClaims();
    case 'mileageOrAgeExceeded':
      dispatch(ratesLoadedFailed(rates));
      dispatch(hideInlineLoader());
      return mileageOrAgeExceeded();
    default:
      return null;
  }
}
